import { useMemo, useCallback, useEffect } from 'react';
import { useCommandPalette } from '@atlassian/jira-command-palette/src/controllers/command-palette/index.tsx';
import { useService } from '@atlassian/jira-common-services/src/use-service/index.tsx';
import {
	ADMIN_PAGE_MODULE,
	COMMAND_PALETTE_MODULE,
	GLOBAL_PAGE_MODULE,
	PERSONAL_SETTINGS_PAGE_MODULE,
	PROJECT_PAGE_MODULE,
	PROJECT_SETTINGS_PAGE_MODULE,
	SERVICEDESK_QUEUE_PAGE_MODULE,
} from '@atlassian/jira-forge-ui-constants/src/constants.tsx';

import { fetchModules } from '@atlassian/jira-forge-ui-utils/src/utils/fetch-modules/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useIsAnonymous } from '@atlassian/jira-tenant-context-controller/src/components/is-anonymous/index.tsx';

import { createLocalStorageProvider } from '@atlassian/jira-browser-storage-providers/src/controllers/local-storage/index.tsx';

const jiraForgeLocalStorage = createLocalStorageProvider('jira-forge-compal');

const MODULES_TO_FETCH = [
	COMMAND_PALETTE_MODULE,
	GLOBAL_PAGE_MODULE,
	ADMIN_PAGE_MODULE,
	PROJECT_PAGE_MODULE,
	PROJECT_SETTINGS_PAGE_MODULE,
	PERSONAL_SETTINGS_PAGE_MODULE,
	SERVICEDESK_QUEUE_PAGE_MODULE,
];

export const useForgeCommandPaletteModules = () => {
	const cloudId = useCloudId();
	const isAnonymous = useIsAnonymous();
	const [{ isOpen }] = useCommandPalette();

	const request = useCallback(
		() =>
			fetchModules({
				cloudId,
				types: MODULES_TO_FETCH,
				isAnonymous,
				includeHidden: false,
				context: {},
			}),
		[cloudId, isAnonymous],
	);

	const { loading, error, data, fetch } = useService(request);

	useEffect(() => {
		if (!isOpen) return;

		const cachedData = getCachedData();

		if (cachedData) return;

		fetch().then((newData) => {
			if (newData) {
				jiraForgeLocalStorage.set(
					CACHE_KEY,
					JSON.stringify({
						data: newData,
						timestamp: Date.now(),
					}),
				);
			}
		});
	}, [fetch, isOpen]);

	return useMemo(() => {
		if (!data) return { loading, error, jiraCommand: [], fullPageModules: [] };
		const { 'jira:command': jiraCommand, ...fullPageModules } = data;

		return {
			jiraCommand,
			fullPageModules: Object.values(fullPageModules).flat(),
			loading,
			error,
		};
	}, [data, error, loading]);
};

export const CACHE_KEY = 'forge-compal-modules-cache';
const CACHE_EXPIRY = 30 * 1000; // 30 seconds

const getCachedData = () => {
	const cached = jiraForgeLocalStorage.get(CACHE_KEY);
	if (cached) {
		const { data, timestamp } = JSON.parse(cached);
		if (Date.now() - timestamp < CACHE_EXPIRY) {
			return data;
		}
	}
	return null;
};
